import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, FileText, Users, Mail, BarChart3, Plus, Trash2, X } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ slug: "", title: "", excerpt: "", content: "", category: "Image SEO", cover_image: "", published: true });

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/admin/login", { replace: true }); return; }
    if (!user.is_admin) { toast.error("You are not an admin."); nav("/", { replace: true }); return; }
    loadAll();
  }, [user, loading]); // eslint-disable-line

  const loadAll = async () => {
    try {
      const [s, p, sb, c] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/blog`),
        axios.get(`${API}/admin/newsletter`),
        axios.get(`${API}/admin/contact`),
      ]);
      setStats(s.data); setPosts(p.data); setSubs(sb.data); setContacts(c.data);
    } catch { toast.error("Failed to load data"); }
  };

  const save = async () => {
    try {
      await axios.post(`${API}/admin/blog`, form);
      toast.success("Post created");
      setShowForm(false);
      setForm({ slug: "", title: "", excerpt: "", content: "", category: "Image SEO", cover_image: "", published: true });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await axios.delete(`${API}/admin/blog/${id}`); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Failed"); }
  };

  if (loading || !user) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Admin</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Signed in as {user.email}</p>
        </div>
        <button onClick={async () => { await logout(); nav("/"); }} data-testid={ADMIN.logoutButton} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-white/10 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: FileText, label: "Posts", value: stats.posts ?? 0 },
          { icon: Users, label: "Subscribers", value: stats.subscribers ?? 0 },
          { icon: Mail, label: "Messages", value: stats.contact_messages ?? 0 },
          { icon: BarChart3, label: "Tool Views", value: stats.total_tool_views ?? 0 },
        ].map((s, i) => {
          const I = s.icon;
          return (
            <div key={i} className="p-5 rounded-xl border border-slate-200 dark:border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 grid place-items-center"><I size={16} /></div>
                <div>
                  <div className="text-2xl font-semibold text-slate-900 dark:text-white">{s.value}</div>
                  <div className="text-xs text-slate-500">{s.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-2 border-b border-slate-200 dark:border-white/10">
        {["dashboard","posts","subscribers","messages","seo"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${tab === t ? "text-blue-600 border-b-2 border-blue-600 -mb-px" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>{t}</button>
        ))}
      </div>

      {tab === "posts" && (
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Blog posts</h2>
            <button onClick={() => setShowForm(true)} data-testid={ADMIN.createPostButton} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"><Plus size={14} /> New post</button>
          </div>
          <div className="mt-4 border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden">
            {posts.length === 0 && <p className="p-6 text-sm text-slate-500">No posts yet.</p>}
            {posts.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3 border-b last:border-b-0 border-slate-200 dark:border-white/10">
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{p.title}</p>
                  <p className="text-xs text-slate-500">/blog/{p.slug} · {p.category}</p>
                </div>
                <button onClick={() => remove(p.id)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg" aria-label="Delete"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "subscribers" && (
        <div className="mt-6 border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden">
          {subs.length === 0 ? <p className="p-6 text-sm text-slate-500">No subscribers.</p> : subs.map((s, i) => (
            <div key={i} className="px-5 py-3 border-b last:border-b-0 border-slate-200 dark:border-white/10 text-sm text-slate-700 dark:text-slate-200 flex justify-between">
              <span>{s.email}</span><span className="text-xs text-slate-400">{new Date(s.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "messages" && (
        <div className="mt-6 space-y-3">
          {contacts.length === 0 ? <p className="text-sm text-slate-500">No messages.</p> : contacts.map((c, i) => (
            <div key={i} className="p-5 rounded-xl border border-slate-200 dark:border-white/10">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-900 dark:text-white">{c.name} <span className="text-slate-400">· {c.email}</span></p>
                <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{c.message}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "seo" && (
        <div className="mt-6 space-y-3">
          <a href={`${API}/seo/sitemap.xml`} target="_blank" rel="noreferrer" className="block p-5 rounded-xl border border-slate-200 dark:border-white/10 hover:border-blue-500/40 transition-colors">
            <p className="font-medium text-slate-900 dark:text-white">View sitemap.xml</p>
            <p className="text-xs text-slate-500 mt-1">Auto-generated from all published pages and blog posts.</p>
          </a>
          <a href={`${API}/seo/robots`} target="_blank" rel="noreferrer" className="block p-5 rounded-xl border border-slate-200 dark:border-white/10 hover:border-blue-500/40 transition-colors">
            <p className="font-medium text-slate-900 dark:text-white">View robots.txt</p>
            <p className="text-xs text-slate-500 mt-1">Search engine crawling directives.</p>
          </a>
        </div>
      )}

      {tab === "dashboard" && (
        <div className="mt-6 p-6 rounded-xl border border-slate-200 dark:border-white/10">
          <p className="text-slate-700 dark:text-slate-200">Welcome back, {user.name.split(" ")[0]}.</p>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Use the tabs above to manage content. Blog posts render publicly under /blog.</p>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white dark:bg-[#121316] rounded-2xl p-6 w-full max-w-2xl border border-slate-200 dark:border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">New blog post</h3>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"><X size={16} /></button>
            </div>
            <div className="mt-4 space-y-3">
              <Field label="Slug (URL)"><input data-testid={ADMIN.postSlugInput} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input" placeholder="my-first-post" /></Field>
              <Field label="Title"><input data-testid={ADMIN.postTitleInput} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" /></Field>
              <Field label="Category">
                <select data-testid={ADMIN.postCategoryInput} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input">
                  {["Image SEO","Compression Guide","WebP Guide","PNG Guide","JPG Guide","Photography Tips","Canva Tips","General"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Cover image URL"><input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="input" /></Field>
              <Field label="Excerpt"><textarea data-testid={ADMIN.postExcerptInput} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} className="input" /></Field>
              <Field label="Content"><textarea data-testid={ADMIN.postContentInput} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8} className="input font-mono text-xs" /></Field>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5">Cancel</button>
              <button onClick={save} data-testid={ADMIN.savePostButton} className="px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">Publish</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.input{width:100%;border-radius:0.5rem;border:1px solid rgb(226 232 240);background:#fff;padding:0.5rem 0.75rem;font-size:0.875rem;color:#0f172a}.dark .input{background:#1a1c20;border-color:rgba(255,255,255,0.1);color:#fff}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1 block">{label}</label>
    {children}
  </div>
);
